import { Link, useParams, useNavigate } from 'react-router-dom'
import { useTemplate, useDeleteTemplate } from '../api/admin-api'
import { useContentReadOnly } from '@/hooks/useConfig'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Pencil, Trash2 } from 'lucide-react'
import { ImageWithFallback } from '@/components/common/ImageWithFallback'
import { I18nTranslationsCard } from '../components/I18nFieldsSection'
import { toast } from 'sonner'
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
  BreadcrumbPage,
} from '@/components/ui/breadcrumb'

export function TemplateDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { data: template, isLoading, error } = useTemplate(id!)
  const deleteMutation = useDeleteTemplate()
  const readOnly = useContentReadOnly()

  const handleDelete = async () => {
    if (!confirm('이 템플릿을 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.')) return

    try {
      await deleteMutation.mutateAsync(id!)
      toast.success('템플릿이 삭제되었습니다')
      navigate('/admin/templates')
    } catch (error: unknown) {
      toast.error(`삭제 실패: ${error instanceof Error ? error.message : '알 수 없는 오류'}`)
    }
  }

  if (isLoading) {
    return <div className="text-center py-12 text-gray-500">로딩 중...</div>
  }

  if (error) {
    return <div className="text-center py-12 text-red-500">에러: {error.message}</div>
  }

  if (!template) return null

  return (
    <div className="max-w-4xl space-y-6">
      {/* Breadcrumb */}
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/admin">어드민</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/admin/templates">템플릿</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>상세</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-heading tracking-tight">{template.name}</h1>
          {template.description && (
            <p className="text-caption mt-2 leading-relaxed">{template.description}</p>
          )}
        </div>
        {!readOnly && (
          <div className="flex gap-2">
            <Link to={`/admin/templates/${id}/edit`}>
              <Button>
                <Pencil className="w-4 h-4 mr-2" />
                수정
              </Button>
            </Link>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              삭제
            </Button>
          </div>
        )}
      </div>

      {/* Main Info */}
      <div className="bg-white rounded-lg border p-6 space-y-6">
        <div className="grid grid-cols-2 gap-6">
          <div>
            <label className="text-sm font-medium text-gray-700">템플릿 카테고리</label>
            <div className="mt-1 text-gray-900">{template.category?.name || '-'}</div>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">비율</label>
            <div className="mt-1 text-gray-900">{template.ratio}</div>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">해상도</label>
            <div className="mt-1 text-gray-900">{template.resolution || '-'}</div>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">길이</label>
            <div className="mt-1 text-gray-900">
              {template.duration ? `${template.duration}초` : '-'}
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">정렬 순서</label>
            <div className="mt-1 text-gray-900">{template.listOrder}</div>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">상태</label>
            <div className="mt-1">
              <Badge variant={template.enabled ? 'success' : 'secondary'}>
                {template.enabled ? '활성' : '비활성'}
              </Badge>
            </div>
          </div>
        </div>

        <div>
          <label className="text-sm font-medium text-gray-700">프롬프트</label>
          <div className="mt-1 p-4 bg-gray-50 rounded-lg text-gray-900 whitespace-pre-wrap font-mono text-sm">
            {template.textPrompt}
          </div>
        </div>

        {template.promptSuffix && (
          <div>
            <label className="text-sm font-medium text-gray-700">프롬프트 접미사</label>
            <div className="mt-1 p-4 bg-gray-50 rounded-lg text-gray-900 whitespace-pre-wrap font-mono text-sm">
              {template.promptSuffix}
            </div>
          </div>
        )}

        {template.thumbnailUrl && (
          <div>
            <label className="text-sm font-medium text-gray-700">썸네일</label>
            <div className="mt-1 max-w-md rounded-lg border bg-slate-100 overflow-hidden flex items-center justify-center">
              <ImageWithFallback
                src={`${template.thumbnailUrl}?v=${new Date(template.updatedAt).getTime()}`}
                alt={template.name}
                className="w-auto max-w-full h-auto max-h-[569px]"
                fallbackClassName="w-32 h-32"
              />
            </div>
          </div>
        )}

        {template.videoUrl && (
          <div>
            <label className="text-sm font-medium text-gray-700">동영상 미리보기</label>
            <div className="mt-1 max-w-md rounded-lg border bg-slate-100 overflow-hidden flex items-center justify-center">
              <video
                src={`${template.videoUrl}?v=${new Date(template.updatedAt).getTime()}`}
                poster={template.thumbnailUrl ? `${template.thumbnailUrl}?v=${new Date(template.updatedAt).getTime()}` : undefined}
                controls
                preload="metadata"
                className="w-auto max-w-full h-auto max-h-[569px]"
              />
            </div>
          </div>
        )}

        {template.frameImages && template.frameImages.length > 0 && (
          <div>
            <label className="text-sm font-medium text-gray-700">
              프레임 이미지 ({template.frameImages.length}개)
            </label>
            <div className="mt-2 grid grid-cols-2 gap-4">
              {template.frameImages.map((frame) => (
                <div key={frame.id} className="border rounded-lg overflow-hidden bg-gray-50">
                  <ImageWithFallback
                    src={`${frame.thumbnailUrl}?v=${new Date(frame.createdAt).getTime()}`}
                    alt="Frame"
                    className="w-full h-32 object-contain"
                  />
                  <div className="text-xs text-center py-1 text-gray-500">
                    {frame.role === 'first_frame' ? '첫 프레임' : '마지막 프레임'}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Translations */}
      <I18nTranslationsCard nameI18n={template.nameI18n} descriptionI18n={template.descriptionI18n} />
    </div>
  )
}
